import { z } from 'zod';

import {
  CARRIER_TYPE,
  type CarrierType,
  CarrierTypeSchema,
  PLAN_FAMILY,
  type PlanFamily,
  PlanFamilySchema,
  type QueuedInsurance,
  QueuedInsuranceSchema,
  SERVICED_CARRIER_TYPES,
  type UnprovidedInsurance,
  UnprovidedInsuranceSchema,
  type UnverifiedInsurance,
  UnverifiedInsuranceSchema,
  type VerifiedInsurance,
  VerifiedInsuranceSchema,
  carrierTypeToFamily,
  carrierTypesShareFamily,
  expandCarrierTypesToPlanFamilies,
  hasCarrierType,
  planFamilyToCarrierTypes,
} from '@/types/insurance.type.ts';

import { ALLOWED_STATES, getState } from '../lib/zipcode';

// TODO This should probably be a class, the more I use it. Then instead of writing
//  multiple updates as:
//    updatedBuilder = setCarrierTypes(setQueuedInsurance(builder, insurance), newTypes);
//  which looks bad cause TS doesn't have a pipe operator instead we could write
//   updatedBuilder = builder
//     .setQueuedInsurance(insurance)
//     .setCarrierTypes(newTypes);
//  Much nicer no? But that requires a refactor and double checking that the builder can be
//  written to zustand stores

// The insurance builder tracks a patient's insurances through the insurance flow
// In the insurance flow:
//  1) discovery runs automatically after patient info collection
//  2) user resolves each plan family (medicare then medicaid) through the 3-outcome flow
//  3) we can only exit the insurance flow when both families are resolved
//     (either with actionable insurance or marked as "not covered")
export const InsuranceBuilderSchema = z.object({
  // Legacy/default flow: asked for patient's insurance carrier types.
  gatheredTypes: z.boolean().catch(false),
  // ran insurance discovery
  ranDiscovery: z.boolean().catch(false),
  // optional step: ran verification on user input
  ranVerification: z.boolean().catch(false),
  // Legacy/default flow: verification fails, then user confirms the data is right.
  ranConfirmation: z.boolean().catch(false),
  // Revamp flow: first verification attempt failed, and user retried the same carrier.
  verificationRetryAttempted: z.boolean().catch(false),

  // Legacy/default flow: which carrier types the patient claims to have.
  carrierTypes: z.array(CarrierTypeSchema).catch([]),

  // Legacy/default flow: carrier types selected before discovery.
  preDiscoveryCarrierTypes: z.array(CarrierTypeSchema).catch([]),

  // Legacy/default flow: user indicated they have commercial/private coverage.
  hasPrivateInsurance: z.boolean().catch(false),

  // which plan families (medicare/medicaid) have been resolved through the flow
  // a family is "resolved" when user confirms/denies coverage for that family
  resolvedFamilies: z.array(PlanFamilySchema).catch([]),

  // which plan families the user claims to have coverage for
  // populated as user resolves each family (empty = not covered for that family)
  coveredFamilies: z.array(PlanFamilySchema).catch([]),

  // insurances queued for verification
  // ideally become verified, otherwise become unverified
  queuedInsurances: z.array(QueuedInsuranceSchema).catch([]),

  // patient's insurances that the api could not verified
  // ideally become confirmed, otherwise become promised or get removed and re-entered as queued
  unverifiedInsurances: z.array(UnverifiedInsuranceSchema).catch([]),

  // patient's insurances they say they have but could not provide info for
  // terminal branch only: does not count as coverage for products or scheduling
  unprovidedInsurances: z.array(UnprovidedInsuranceSchema).catch([]),

  // insurances accepted as proof through the insurance upload fallback
  confirmedInsurances: z.array(UnverifiedInsuranceSchema).catch([]),

  // api verified insurances, the ones we love
  verifiedInsurances: z.array(VerifiedInsuranceSchema).catch([]),

  // Revamp flow: non-actionable discovery snapshot kept for review prompts,
  // even if the user later verifies a different plan in the same family.
  discoveredInsurances: z.array(VerifiedInsuranceSchema).catch([]),
});

export type InsuranceBuilder = z.infer<typeof InsuranceBuilderSchema>;

export const initialInsuranceBuilderState: InsuranceBuilder = {
  gatheredTypes: false,
  ranDiscovery: false,
  ranVerification: false,
  ranConfirmation: false,
  verificationRetryAttempted: false,
  carrierTypes: [],
  preDiscoveryCarrierTypes: [],
  hasPrivateInsurance: false,
  resolvedFamilies: [],
  coveredFamilies: [],
  queuedInsurances: [],
  unverifiedInsurances: [],
  unprovidedInsurances: [],
  confirmedInsurances: [],
  verifiedInsurances: [],
  discoveredInsurances: [],
};

export function reset(builder: InsuranceBuilder): InsuranceBuilder {
  return {
    ...initialInsuranceBuilderState,
    preDiscoveryCarrierTypes: builder.preDiscoveryCarrierTypes,
    carrierTypes: builder.carrierTypes,
    gatheredTypes: builder.gatheredTypes,
    hasPrivateInsurance: builder.hasPrivateInsurance,
  };
}

/**
 * Gets all actionable insurances (confirmed + verified).
 */
export function getActionableInsurances(
  builder: InsuranceBuilder
): (UnverifiedInsurance | VerifiedInsurance)[] {
  const actionableInsurances = [
    ...builder.confirmedInsurances,
    ...builder.verifiedInsurances,
  ].filter((i) => SERVICED_CARRIER_TYPES.has(i.carrier.carrierType));

  if (builder.resolvedFamilies.length === 0) {
    return actionableInsurances;
  }

  return actionableInsurances.filter((insurance) =>
    builder.coveredFamilies.includes(carrierTypeToFamily(insurance.carrier.carrierType))
  );
}

/**
 * Gets the set of carrier types that have actionable insurance.
 * Used for product filtering - products are eligible if they support any of these types.
 */
function getActionableCarrierTypes(builder: InsuranceBuilder): Set<CarrierType> {
  const types = new Set<CarrierType>(builder.unprovidedInsurances.map((i) => i.carrierType));

  for (const insurance of getActionableInsurances(builder)) {
    types.add(insurance.carrier.carrierType);
  }

  return types;
}

/**
 * Gets actionable coverage expanded to the full Medicare/Medicaid family.
 * Product selection is family-based, so managed coverage should expose products
 * keyed to its base family and vice versa.
 */
export function getActionablePlanFamilyCarrierTypes(builder: InsuranceBuilder): Set<CarrierType> {
  return expandCarrierTypesToPlanFamilies(getActionableCarrierTypes(builder));
}

export function setUnprovidedInsurance(
  builder: InsuranceBuilder,
  insurance: UnprovidedInsurance
): InsuranceBuilder {
  const typeClearedBuilder = removeAllOfFamily(builder, insurance.carrierType);
  return {
    ...typeClearedBuilder,
    unprovidedInsurances: [...typeClearedBuilder.unprovidedInsurances, insurance],
  };
}

export function setUnverifiedInsurances(
  builder: InsuranceBuilder,
  ...insurances: UnverifiedInsurance[]
): InsuranceBuilder {
  return insurances.reduce(
    (acc, insurance) => ({
      ...removeAllOfFamily(acc, insurance.carrier.carrierType),
      unverifiedInsurances: [
        ...acc.unverifiedInsurances.filter(
          (i) => !hasCarrierType(i, insurance.carrier.carrierType)
        ),
        insurance,
      ],
    }),
    builder
  );
}

export function setQueuedInsurance(
  builder: InsuranceBuilder,
  insurance: QueuedInsurance
): InsuranceBuilder {
  const isRetryingSameCarrier = builder.unverifiedInsurances.some((i) =>
    hasCarrierType(i, insurance.carrier.carrierType)
  );

  return {
    ...removeAllOfFamily(builder, insurance.carrier.carrierType),
    verificationRetryAttempted: isRetryingSameCarrier ? builder.verificationRetryAttempted : false,
    queuedInsurances: [
      ...builder.queuedInsurances.filter((i) => !hasCarrierType(i, insurance.carrier.carrierType)),
      insurance,
    ],
  };
}

export function setConfirmedInsurances(
  builder: InsuranceBuilder,
  ...insurances: UnverifiedInsurance[]
): InsuranceBuilder {
  return insurances.reduce(
    (acc, insurance) => ({
      ...removeAllOfFamily(acc, insurance.carrier.carrierType),
      confirmedInsurances: [
        ...acc.confirmedInsurances.filter((i) => !hasCarrierType(i, insurance.carrier.carrierType)),
        insurance,
      ],
    }),
    builder
  );
}

export function setVerifiedInsurances(
  builder: InsuranceBuilder,
  ...insurances: VerifiedInsurance[]
): InsuranceBuilder {
  const familiesClearedBuilder = insurances.reduce(
    (acc, insurance) => removeAllOfFamily(acc, insurance.carrier.carrierType),
    builder
  );

  return {
    ...familiesClearedBuilder,
    verifiedInsurances: [...familiesClearedBuilder.verifiedInsurances, ...insurances],
  };
}

function getFamilyStatus(
  builder: InsuranceBuilder,
  family: PlanFamily
): 'verified' | 'unverified' | 'unknown' | 'not covered' {
  const { base, managed } = planFamilyToCarrierTypes(family);
  const relevantTypes = [base, managed];

  const hasVerifiedInsurance = builder.verifiedInsurances.some((i) =>
    relevantTypes.includes(i.carrier.carrierType)
  );
  const hasConfirmed = builder.confirmedInsurances.some((i) =>
    relevantTypes.includes(i.carrier.carrierType)
  );
  const hasUnverified = builder.unverifiedInsurances.some((i) =>
    relevantTypes.includes(i.carrier.carrierType)
  );
  const hasQueued = builder.queuedInsurances.some((i) =>
    relevantTypes.includes(i.carrier.carrierType)
  );
  const hasUnverifiedOrPendingInsurance = hasConfirmed || hasUnverified || hasQueued;
  const hasAnsweredFamilyQuestion = builder.resolvedFamilies.includes(family);
  const hasClaimedCoverageWithoutInsurance = builder.coveredFamilies.includes(family);

  if (hasAnsweredFamilyQuestion && !builder.coveredFamilies.includes(family)) return 'not covered';

  if (hasVerifiedInsurance) return 'verified';
  if (hasUnverifiedOrPendingInsurance) return 'unverified';

  // Unknown means we either have not asked this family yet, or the user said they
  // have coverage but we do not have any insurance details to act on.
  if (!hasAnsweredFamilyQuestion || hasClaimedCoverageWithoutInsurance) return 'unknown';

  return 'not covered';
}

function getLegacyCarrierStatus(
  builder: InsuranceBuilder,
  carrierType: CarrierType
): 'verified' | 'unverified' | 'unknown' | 'not selected' {
  const hasVerified = builder.verifiedInsurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, carrierType)
  );
  const hasConfirmed = builder.confirmedInsurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, carrierType)
  );
  const hasUnverified = builder.unverifiedInsurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, carrierType)
  );
  const hasQueued = builder.queuedInsurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, carrierType)
  );
  const hasInCarrierTypes = builder.carrierTypes.some((type) =>
    carrierTypesShareFamily(type, carrierType)
  );

  if (hasVerified) return 'verified';
  if (hasConfirmed || hasUnverified || hasQueued) return 'unverified';
  if (hasInCarrierTypes) return 'unknown';
  return 'not selected';
}

//
// Private helpers
//
function removeAllOfFamily(builder: InsuranceBuilder, carrierType: CarrierType): InsuranceBuilder {
  const familyTypes = Object.values(planFamilyToCarrierTypes(carrierTypeToFamily(carrierType)));

  return {
    ...builder,
    unverifiedInsurances: builder.unverifiedInsurances.filter(
      (i) => !familyTypes.includes(i.carrier.carrierType)
    ),
    unprovidedInsurances: builder.unprovidedInsurances.filter(
      (i) => !familyTypes.includes(i.carrierType)
    ),
    queuedInsurances: builder.queuedInsurances.filter(
      (i) => !familyTypes.includes(i.carrier.carrierType)
    ),
    confirmedInsurances: builder.confirmedInsurances.filter(
      (i) => !familyTypes.includes(i.carrier.carrierType)
    ),
    verifiedInsurances: builder.verifiedInsurances.filter(
      (i) => !familyTypes.includes(i.carrier.carrierType)
    ),
  };
}

/**
Returns the base Customer IO event attributes for the insurance eligibility event.

@returns The Customer IO event attributes
*/
export function getBaseInsuranceEligibilityEventAttributes(
  builder: InsuranceBuilder,
  zipCode: string,
  options: { usePlanFamilyStatus?: boolean } = {}
) {
  const medicaidStatus = options.usePlanFamilyStatus
    ? getFamilyStatus(builder, PLAN_FAMILY.MEDICAID)
    : getLegacyCarrierStatus(builder, CARRIER_TYPE.MEDICAID);
  const medicareStatus = options.usePlanFamilyStatus
    ? getFamilyStatus(builder, PLAN_FAMILY.MEDICARE)
    : getLegacyCarrierStatus(builder, CARRIER_TYPE.MEDICARE);
  const actionableInsurances = getActionableInsurances(builder);

  // Find insurance for each family (prefer managed types)
  const medicaidInsurance = actionableInsurances.find(
    (i) =>
      i.carrier.carrierType === CARRIER_TYPE.MEDICAID_MANAGED_CARE ||
      i.carrier.carrierType === CARRIER_TYPE.MEDICAID
  );
  const medicareInsurance = actionableInsurances.find(
    (i) =>
      i.carrier.carrierType === CARRIER_TYPE.MEDICARE_ADVANTAGE ||
      i.carrier.carrierType === CARRIER_TYPE.MEDICARE
  );

  const insurancePlanNames = actionableInsurances.map((i) => i.carrier.name);

  return {
    insurance_zipcode: zipCode,
    insurance_plan_names: insurancePlanNames,
    medicaid_id: medicaidInsurance?.memberId,
    medicare_id: medicareInsurance?.memberId,
    medicaid_status: medicaidStatus,
    medicare_status: medicareStatus,
  };
}

export function isEligibleZipCode(zipCode: string): boolean {
  const state = getState(zipCode);
  return state ? ALLOWED_STATES.has(state) : false;
}
